// src/components/WeatherChart/WeatherChart.js
import React, { useEffect, useState } from 'react';
import '../CSS/Weather.css';
import Plotly from 'plotly.js-dist-min';
import apiClient from '../lib/api-client';
import { useTheme } from '../ThemeContext';
import { useWindowWidth } from '../lib/useWindowWidth';

const WeatherAnalysis = ({ currentRegion,
  setCurrentChartType,
  currentChartType,
  countyData,
  isToday,
  startDate,
  endDate,
  timezone, }) => {
  const [weatherData, setWeatherData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timeValues, setTimeValues] = useState([]);

  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;

  // Fetch weather data when countyData or currentChartType changes
  useEffect(() => {
    const fetchWeatherData = async () => {
      if (!countyData) {
        setWeatherData(null);
        return;
      }

      try {
        setIsLoading(true);
        // Clear existing chart
        Plotly.purge('plot');

        const weatherType = currentChartType === 'wind_speed' ? 'wind_speed' :
          // currentChartType === 'weather_code' ? 'weather_code' :
          currentChartType === 'wind_gusts' ? 'wind_gusts' :
            currentChartType === 'temperature' ? 'temperature' :
              currentChartType === 'precipitation' ? 'precipitation' :
                'wind_direction';

        const response = await apiClient.getHourlyWeather(startDate, timezone, weatherType, countyData.county_id);
        console.log('Weather hourly data:', response);

        // Extract and format time values from API response
        const times = Object.keys(response.data).map(timeStr => {
          const date = new Date(timeStr);
          return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        });
        setTimeValues(times);

        // Format the API response data and store the unit
        const formattedData = {
          mean: Object.values(response.data), // Extract values from the time-based keys
          unit: response.unit || '' // Store the unit from the API
        };

        setWeatherData(formattedData);
      } catch (error) {
        console.error('Error fetching weather data:', error);
        setWeatherData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWeatherData();
  }, [countyData, isToday, currentChartType, startDate]);

  // Use weatherData if available, otherwise use default data
  const wind_gust_data = {
    max: [],
    mean: [],
    min: []
  };

  const wind_speed_data = {
    max: [],
    mean: [],
    min: []
  };

  const weather_code_data = {
    max: [],
    mean: [],
    min: []
  };

  const temperature_data = {
    max: [],
    mean: [],
    min: []
  };

  const precipitation_data = {
    max: [],
    mean: [],
    min: []
  };

  const wind_direction_data = {
    max: [],
    mean: [],
    min: []
  };


  useEffect(() => {
    if (countyData) {
      console.log('Received county data:', countyData);
      // You can use countyData here to update the chart
      console.log("Current Chart Type", currentChartType);
      updateChart(currentChartType, countyData.id);
    }
  }, [countyData, currentChartType, weatherData, isDarkMode, isMobile]);

  const updateChart = (type, regionId = null) => {
    const title = type === 'wind_speed' ? 'Wind Speed Throughout the Day' :
      // type === 'weather_code' ? 'Weather Code Throughout the Day' :
      type === 'wind_gusts' ? 'Wind Gusts Throughout the Day' :
        type === 'temperature' ? 'Temperature Throughout the Day' :
          type === 'precipitation' ? 'Precipitation Throughout the Day' :
            'Wind Direction Throughout the Day';
    const mobileTitle = title.replace(' Throughout the Day', '');

    // Get the unit from the API response if available
    const unit = weatherData?.unit || '';

    // Build the y-axis label with the unit from the API or fallback to default
    const yLabel = type === 'wind_speed' ? `Wind Speed (${unit || 'm/s'})` :
      // type === 'weather_code' ? 'Weather Code' :
      type === 'wind_gusts' ? `Wind Gusts (${unit || 'm/s'})` :
        type === 'temperature' ? `Temperature (${unit || '°F'})` :
          type === 'precipitation' ? `Precipitation (${unit || 'mm'})` :
            `Wind Direction (${unit || 'degrees'})`;

    // Use weatherData if available, otherwise use default data
    let dataset;
    if (weatherData) {
      // If we have API data, only show mean values
      dataset = {
        mean: weatherData.mean
      };
    } else {
      dataset =
        (type === 'wind_speed' ? wind_speed_data :
          // type === 'weather_code' ? weather_code_data :
          type === 'wind_gusts' ? wind_gust_data :
            type === 'temperature' ? temperature_data :
              type === 'precipitation' ? precipitation_data :
                wind_direction_data);
    }

    let colors = type === 'wind_speed' || type === 'wind_gusts' || type === 'wind_direction' ?
      ['#3b82f6'] :
      type === 'temperature' ?
        ['#ef4444'] :
        type === 'precipitation' ?
          ['#22c55e'] :
          ['#8b5cf6'];

    // Create traces based on available data
    let traces = [];
    if (weatherData) {
      // If we have API data, only show mean values
      traces.push({
        x: timeValues, // Use dynamic time values
        y: dataset.mean,
        type: 'scatter',
        mode: 'lines+markers',
        marker: {
          color: colors[0],
          size: 8,
          line: {
            color: '#fff',
            width: 2
          }
        },
        line: {
          width: 3,
          shape: 'spline'
        },
        name: 'Mean'
      });
    } else {
      // If using default data, show min, mean, and max
      traces = ['max', 'mean', 'min'].map((stat, i) => ({
        x: timeValues, // Use dynamic time values
        y: dataset[stat],
        type: 'scatter',
        mode: 'lines+markers',
        marker: {
          color: colors[i],
          size: 8,
          line: {
            color: '#fff',
            width: 2
          }
        },
        line: {
          width: 3,
          shape: 'spline'
        },
        name: stat.charAt(0).toUpperCase() + stat.slice(1)
      }));
    }

    let layout = {
      title: {
        text: countyData ? `${isMobile ? mobileTitle : title} - ${countyData.name}` : title,
        font: {
          size: isMobile ? 12 : 16,
          color: isDarkMode ? '#e2e8f0' : '#1f2937'
        }
      },
      xaxis: {
        title: {
          text: 'Time',
          font: {
            size: isMobile ? 11 : 14,
            color: isDarkMode ? '#e2e8f0' : '#1f2937'
          },
          standoff: isMobile ? 8 : 15
        },
        gridcolor: isDarkMode ? '#334155' : '#f1f5f9',
        zeroline: false,
        tickangle: 45,
        tickfont: {
          size: isMobile ? 10 : 12,
          color: isDarkMode ? '#cbd5e1' : '#475569'
        },
        showgrid: true
      },
      yaxis: {
        title: {
          text: yLabel,
          font: {
            size: isMobile ? 11 : 14,
            color: isDarkMode ? '#e2e8f0' : '#1f2937'
          },
          standoff: isMobile ? 8 : 15
        },
        gridcolor: isDarkMode ? '#334155' : '#f1f5f9',
        zeroline: false,
        tickfont: {
          size: isMobile ? 10 : 12,
          color: isDarkMode ? '#cbd5e1' : '#475569'
        },
        showgrid: true,
        automargin: true  // Ensure there's enough margin for the title
      },
      margin: {
        t: isMobile ? 38 : 50,
        l: isMobile ? 50 : 80,
        r: isMobile ? 16 : 30,
        b: isMobile ? 64 : 80
      },
      legend: {
        x: 1,
        xanchor: 'right',
        y: 1,
        bgcolor: isDarkMode ? 'rgba(30,41,59,0.8)' : 'rgba(255,255,255,0.8)',
        bordercolor: isDarkMode ? '#475569' : '#e2e8f0',
        borderwidth: 1,
        borderradius: 4,
        font: {
          size: isMobile ? 10 : 12,
          color: isDarkMode ? '#e2e8f0' : '#1f2937'
        }
      },
      plot_bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
      paper_bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
      hovermode: 'x unified',
      hoverlabel: {
        bgcolor: isDarkMode ? '#475569' : '#334155',
        font: { color: '#ffffff' }
      }
    };

    Plotly.newPlot('plot', traces, layout, {
      responsive: true,
      displayModeBar: !isMobile,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d']
    });
  };

  return (
    <div className="container-fluid mt-4 mb-5">
      <div className={`card ${isDarkMode ? 'bg-dark border-secondary text-light' : 'card-custom'}`}>
        <div className={`card-header ${isDarkMode ? 'bg-dark border-secondary' : 'bg-white border-bottom'} weather-card-header-row p-3`}>
          <div>
            <small className={`fw-bold ${isDarkMode ? 'text-light' : 'text-main'}`} id="selected-region">
              {countyData ? `Hourly weather data for ${countyData.name}` : 'Showing data for all regions'}
            </small>
          </div>
          <div className="d-flex gap-2">
            <div className="dropdown-container">
              <select
                className={`form-select ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}
                value={currentChartType}
                onChange={(e) => {
                  // Clear existing chart before changing type
                  Plotly.purge('plot');
                  setCurrentChartType(e.target.value);
                }}
                disabled={isLoading || !countyData}
              >
                <option value="wind_speed">Wind Speed</option>
                {/* <option value="weather_code">Weather Code</option> */}
                <option value="wind_gusts">Wind Gusts</option>
                <option value="temperature">Temperature</option>
                <option value="precipitation">Precipitation</option>
                <option value="wind_direction">Wind Direction</option>
              </select>
            </div>
          </div>
        </div>
        <div className={`card-body ${isDarkMode ? 'bg-dark text-light' : 'bg-light'} p-4 weather-analysis-body`}>
          {countyData ? (
            <div className="position-relative weather-chart-container">
              {isLoading && (
                <div className="position-absolute top-50 start-50 translate-middle">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
              <div id="plot" style={{ height: '400px', opacity: isLoading ? 0.5 : 1 }} className={`${isDarkMode ? 'bg-dark' : 'bg-white'} rounded shadow-sm`}></div>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <h4 className="text-muted weather-empty-title">Select a County to populate the chart</h4>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeatherAnalysis;